import { Box, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { getUrl, uploadData } from "aws-amplify/storage";
import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReportWrapper from "../../abstract/Report";
import BottomBar from "../../components/report/BottomBar";
import AverageEggsChart from "../../components/report/charts/AverageEggsChart";
import ProcessSteps from "../../components/report/charts/ProcessSteps";
import CycleCard from "../../components/report/CycleCard";
import CycleTable from "../../components/report/CycleTable";
import ReportFooterDialog from "../../components/report/ReportFooterDialog";

import SubmitButton from "../../components/SubmitButton";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { useReport } from "../../contexts/ReportContext";
import { reportPageWidth } from "../../types";
import InfoCard from "../EggFreezeCalculator/InfoCard";
import {
  ReportOverrideFooter
} from "../EggFreezeCalculator/types";
import { CustomizeReportRequestData, CustomizeReportResponse } from "./types";
// import { texts } from "../../pages/EggFreezeCalculator/Report";
import PatientInfoCard from "../EggFreezeCalculator/PatientInfoCard";

const defaultContactDetails: ReportOverrideFooter = {
  address: "123 Main St, San Francisco, CA 94107",
  phone: "(123) 456-7890",
  website: "www.example.com",
};

const Report: React.FC = () => {
  const [logo, setLogo] = React.useState<string | null>(null);
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
  const [contactDetails, setContactDetails] =
    React.useState<ReportOverrideFooter | null>(null);

  const { user, session } = useAuth();
  const { apiService } = useApi();
  const { updateBreadcrumbs, translate, updateActions } = useApp();
  const navigate = useNavigate();
  const { showError, showSuccess } = useError();

  const { colorStyle, updateColorStyle } = useReport();

  const { isLoading, setSubmitting } = useApi();
  const generalTranslations = translate("general");
  const eggFreezeTranslations = translate("eggfreeze");
  const translations = translate("customize");

  useEffect(() => {
    updateBreadcrumbs([
      {
        label: translations.title,
        onClick: () => {
          navigate("/report");
        },
      },
    ]);
    updateActions([
      <SubmitButton key={"save"} color={"blackButton"} onClick={save}>
        {generalTranslations.form.buttons.save}
      </SubmitButton>,
    ]);

    return () => {
      updateBreadcrumbs([]);
      updateActions([]);
    };
  }, [logo, colorStyle, contactDetails, isLoading]);

  useEffect(() => {
    if (user?.config) {
      if (user.config.color_scheme) {
        updateColorStyle(user.config.color_scheme);
      }

      if (user.config.logo) {
        setLogo(user.config.logo);
        getUrl({ path: user.config.logo }).then((urlResponse) => {
          setLogoUrl(urlResponse.url.toString());
        });
      }

      if (user.config.contact_details) {
        setContactDetails(user.config.contact_details);
      } else {
        setContactDetails(defaultContactDetails);
      }
    }
  }, [user]);

  const handleLogoChange = async (file: File | null) => {
    if (file) {
      const logoPath = `private/${user.clinic}/logo.png`;
      await uploadData({
        data: file,
        path: logoPath,
      });
      const urlResponse = await getUrl({ path: logoPath });
      setLogoUrl(urlResponse.url.href);
      console.info("logo url response", urlResponse);
      setLogo(logoPath);
    }
  };

  const save = useCallback(async () => {
    try {
      setSubmitting(true);
      const config: any = user?.config ?? {};
      config["color_scheme"] = colorStyle;
      config["logo"] = logo;
      config["contact_details"] = contactDetails;
      await apiService.post<
        CustomizeReportResponse,
        CustomizeReportRequestData
      >("/user", {
        config: JSON.stringify(config),
      });
      showSuccess(translations.messages.save);
    } catch (error) {
      showError(translations.errors.save);
    } finally {
      setSubmitting(false);
    }
  }, [colorStyle, logoUrl, logo, contactDetails, user]);

  const saveFooterDetails = useCallback(
    (address: string, phone: string, website: string) => {
      setContactDetails({ address, phone, website });
    },
    [contactDetails, logo, colorStyle]
  );

  const deleteLogo = async () => {
    setLogo(null);
    setLogoUrl(null);
  };

  return (
    <React.Fragment>
      <ReportWrapper
        title={translations.title}
        type={"report"}
        sections={[
          {
            title: eggFreezeTranslations.report.sections.patientDetails,
            component: (
              <PatientInfoCard
                type="report"
                patientInfo={{
                  name: "John Doe",
                  weight: `120 kg`,
                  age: `24`,
                  ethnicity: translate("general", `ethnicities.asian`),
                  height: `182 cm`,
                  amh: "12.2",
                }}
              />
            ),
          },
          {
            title: eggFreezeTranslations.report.sections.yourEstimatedResults,
            component: (
              <InfoCard content={eggFreezeTranslations.report.texts.estimatedResults} />
            ),
          },
          {
            component: (
              <Stack
                justifyContent={"space-between"}
                direction={"row"}
                spacing={2}
              >
                <CycleCard cycle={1} eggs={2} liveBirthChance={[1, 2]} />
                <CycleCard cycle={2} eggs={1} liveBirthChance={[1, 2]} />
                <CycleCard cycle={3} eggs={1} liveBirthChance={[1, 2]} />
              </Stack>
            ),
          },
          {
            title:
              eggFreezeTranslations.report.sections.estimatedResultsInYears,
            component: (
              <Stack>
                <Box>
                  <CycleTable
                    cycleData={[
                      {
                        cycle: `1 cycle`,
                        eggs: "12",
                        liveBirth1: 1,
                        liveBirth2: 2,
                      },
                      {
                        cycle: `2 cycle`,
                        eggs: "13",
                        liveBirth1: 1,
                        liveBirth2: 4,
                      },
                    ]}
                  />
                </Box>

                <Stack sx={{ marginTop: 2 }} gap={1}>
                  <Typography variant="report.title">
                    {eggFreezeTranslations.report.subtitles.whyManyEggs}
                  </Typography>
                  <Typography variant="report.info.label" color={"grey"}>
                    {eggFreezeTranslations.report.texts.ivfProcess}
                  </Typography>
                </Stack>
              </Stack>
            ),
          },
          {
            title: eggFreezeTranslations.report.sections.processSteps,
            component: (
              <ProcessSteps
                age={30}
                attrition={{
                  retrieved: 30,
                  frozen: 20,
                  thawed: 18,
                  fertilized: 4,
                  good_embryos: 4,
                  implanted: 3,
                  livebirth: 1,
                }}
                attritionGraph={{
                  retrieved: { left: 80, middle: 70, right: 69 },
                  frozen: { left: 69, middle: 60, right: 50 },
                  thawed: { left: 50, middle: 40, right: 30 },
                  fertilized: { left: 30, middle: 25, right: 20 },
                  good_embryos: { left: 20, middle: 15, right: 10 },
                  implanted: { left: 10, middle: 8, right: 5 },
                  livebirth: { left: 5, middle: 4, right: 2 },
                }}
              />
            ),
          },
          {
            title: "",
            component: (
              <Stack spacing={1}>
                <Typography variant="report.title">
                  {eggFreezeTranslations.report.subtitles.averageEggsRetrieved}
                </Typography>
                <Typography variant="report.info.label">
                  {eggFreezeTranslations.report.texts.eggsByAge}
                </Typography>
              </Stack>
            ),
          },
          {
            title: eggFreezeTranslations.report.sections.averageEggsRetrieved,
            component: (
              <Box>
                <AverageEggsChart
                  age={24}
                  eggs={12}
                  data={[
                    {
                      age: 20,
                      error: [8, 12],
                      numEggs: 10,
                      numEggsLow: 8,
                      numEggsHigh: 12,
                    },
                    {
                      age: 22,
                      error: [7, 11],
                      numEggs: 9,
                      numEggsLow: 7,
                      numEggsHigh: 11,
                    },
                    {
                      age: 24,
                      error: [6, 10],
                      numEggs: 8,
                      numEggsLow: 6,
                      numEggsHigh: 10,
                    },
                    {
                      age: 26,
                      error: [5, 9],
                      numEggs: 7,
                      numEggsLow: 5,
                      numEggsHigh: 9,
                    },
                    {
                      age: 28,
                      error: [4, 8],
                      numEggs: 6,
                      numEggsLow: 4,
                      numEggsHigh: 8,
                    },
                    {
                      age: 30,
                      error: [3, 7],
                      numEggs: 5,
                      numEggsLow: 3,
                      numEggsHigh: 7,
                    },
                    {
                      age: 32,
                      error: [2, 6],
                      numEggs: 4,
                      numEggsLow: 2,
                      numEggsHigh: 6,
                    },
                    {
                      age: 34,
                      error: [1, 5],
                      numEggs: 3,
                      numEggsLow: 1,
                      numEggsHigh: 5,
                    },
                    {
                      age: 36,
                      error: [1, 4],
                      numEggs: 2.5,
                      numEggsLow: 1,
                      numEggsHigh: 4,
                    },
                    {
                      age: 38,
                      error: [0, 3],
                      numEggs: 2,
                      numEggsLow: 0,
                      numEggsHigh: 3,
                    },
                    {
                      age: 40,
                      error: [0, 2],
                      numEggs: 1.5,
                      numEggsLow: 0,
                      numEggsHigh: 2,
                    },
                    {
                      age: 42,
                      error: [0, 1.5],
                      numEggs: 1,
                      numEggsLow: 0,
                      numEggsHigh: 1.5,
                    },
                    {
                      age: 44,
                      error: [0, 1],
                      numEggs: 0.5,
                      numEggsLow: 0,
                      numEggsHigh: 1,
                    },
                  ]}
                  width={reportPageWidth}
                  height={500}
                />
              </Box>
            ),
          },
        ]}
      />
      <ReportFooterDialog
        address={contactDetails?.address ?? ""}
        phone={contactDetails?.phone ?? ""}
        website={contactDetails?.website ?? ""}
        onSubmit={saveFooterDetails}
      />
      <BottomBar />
    </React.Fragment>
  );
};

export default Report;
